import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api, API, useAuth } from '../App';
import { Plus, Pencil, Trash2, X, Users, User } from 'lucide-react';

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const BOSH = { ad: '', soyad: '', email: '', telefon: '', sifre: '', rol: 'personel', kurum_id: '', aktif: true };

export default function Kullanicilar() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [kurumlar, setKurumlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(BOSH);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [api.get('/kullanicilar')];
      if (user.rol === 'superadmin') reqs.push(api.get('/kurumlar'));
      const [u, k] = await Promise.all(reqs);
      setList(u.data);
      if (k) setKurumlar(k.data);
    } catch { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEkle = () => {
    setForm({ ...BOSH, kurum_id: user.rol === 'superadmin' ? '' : user.kurum_id });
    setModal('ekle');
  };

  const openDuzenle = u => {
    setForm({ ad: u.ad, soyad: u.soyad, email: u.email, telefon: u.telefon || '', sifre: '', rol: u.rol, kurum_id: u.kurum_id, aktif: u.aktif });
    setModal(u);
  };

  const handleSave = async e => {
    e.preventDefault();
    if (modal === 'ekle' && form.sifre.length < 6) return toast.error('Şifre en az 6 karakter olmalı');
    setSaving(true);
    try {
      if (modal === 'ekle') {
        await api.post('/kullanicilar', form);
        toast.success('Kullanıcı oluşturuldu');
      } else {
        const data = { ...form };
        if (!data.sifre) delete data.sifre;
        await api.put(`/kullanicilar/${modal.id}`, data);
        toast.success('Kullanıcı güncellendi');
      }
      setModal(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Hata'); }
    finally { setSaving(false); }
  };

  const handleDelete = async u => {
    if (!window.confirm(`"${u.ad} ${u.soyad}" kullanıcısını silmek istiyor musunuz?`)) return;
    try { await api.delete(`/kullanicilar/${u.id}`); toast.success('Silindi'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Silinemedi'); }
  };

  const filtered = list.filter(u => {
    const q = search.toLowerCase();
    return `${u.ad} ${u.soyad} ${u.email}`.toLowerCase().includes(q);
  });

  const rolBadge = rol => {
    if (rol === 'superadmin') return 'badge-red';
    if (rol === 'yonetici') return 'badge-blue';
    return 'badge-green';
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Kullanıcılar</h1>
          <p className="page-subtitle">{list.length} kullanıcı kayıtlı</p>
        </div>
        <button className="btn btn-primary" onClick={openEkle}>
          <Plus size={16} /> Yeni Kullanıcı
        </button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div className="search-box" style={{ maxWidth: 320 }}>
          <span className="search-icon">
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          </span>
          <input className="input" placeholder="Ad, soyad veya email ara..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-blue" style={{ width: 32, height: 32 }} />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Kullanıcı</th>
                  {user.rol === 'superadmin' && <th>Kurum</th>}
                  <th>Rol</th>
                  <th>Telefon</th>
                  <th>Durum</th>
                  <th>Kayıt</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {u.profil_resmi
                          ? <img src={`${API.replace('/api','')}/uploads/profil/${u.profil_resmi}`} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                          : <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                              {u.ad?.[0]}{u.soyad?.[0]}
                            </div>
                        }
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{u.ad} {u.soyad}</div>
                          <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    {user.rol === 'superadmin' && <td style={{ fontSize: 13 }}>{u.kurum_adi || '—'}</td>}
                    <td><span className={`badge ${rolBadge(u.rol)}`}>{u.rol}</span></td>
                    <td style={{ fontSize: 13, color: 'var(--gray-600)' }}>{u.telefon || '—'}</td>
                    <td><span className={`badge ${u.aktif ? 'badge-green' : 'badge-gray'}`}>{u.aktif ? 'Aktif' : 'Pasif'}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--gray-400)' }}>{new Date(u.olusturma_tarihi).toLocaleDateString('tr-TR')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openDuzenle(u)}><Pencil size={13} /></button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(u)}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>
                    {search ? 'Arama sonucu bulunamadı' : 'Henüz kullanıcı yok'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <Modal title={modal === 'ekle' ? 'Yeni Kullanıcı' : 'Kullanıcıyı Düzenle'} onClose={() => setModal(null)}>
          <form onSubmit={handleSave}>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {user.rol === 'superadmin' && modal === 'ekle' && (
                <div className="input-group">
                  <label className="input-label">Kurum *</label>
                  <select className="input" required value={form.kurum_id} onChange={e => setForm({...form, kurum_id: e.target.value})}>
                    <option value="">Seçin...</option>
                    {kurumlar.map(k => <option key={k.id} value={k.id}>{k.ad}</option>)}
                  </select>
                </div>
              )}
              <div className="form-grid">
                <div className="input-group">
                  <label className="input-label">Ad *</label>
                  <input className="input" required value={form.ad} onChange={e => setForm({...form, ad: e.target.value})} />
                </div>
                <div className="input-group">
                  <label className="input-label">Soyad *</label>
                  <input className="input" required value={form.soyad} onChange={e => setForm({...form, soyad: e.target.value})} />
                </div>
                <div className="input-group full">
                  <label className="input-label">E-posta *</label>
                  <input className="input" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                </div>
                <div className="input-group">
                  <label className="input-label">Telefon</label>
                  <input className="input" value={form.telefon} onChange={e => setForm({...form, telefon: e.target.value})} placeholder="05xx..." />
                </div>
                <div className="input-group">
                  <label className="input-label">Rol</label>
                  <select className="input" value={form.rol} onChange={e => setForm({...form, rol: e.target.value})}>
                    <option value="personel">Personel</option>
                    <option value="yonetici">Yönetici</option>
                  </select>
                </div>
                <div className="input-group full">
                  <label className="input-label">{modal === 'ekle' ? 'Şifre *' : 'Yeni Şifre (boş bırakılabilir)'}</label>
                  <input className="input" type="password" value={form.sifre} onChange={e => setForm({...form, sifre: e.target.value})} placeholder="En az 6 karakter" required={modal === 'ekle'} />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.aktif} onChange={e => setForm({...form, aktif: e.target.checked})} />
                <span className="input-label" style={{ margin: 0 }}>Aktif</span>
              </label>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>İptal</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <div className="spinner" /> : null} Kaydet
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
